/**
 * Runtime validation and defaults for the save document.
 *
 * The runtime half of `save.ts`, same split as `level-guards.ts` is to
 * `level.ts` and for the same reason (see that file's header): the validator is
 * what keeps the type from drifting from what a saved document can actually
 * contain.
 *
 * `Fault`, `Validated` and the `Checker` shape are duplicated from
 * `level-guards.ts` rather than imported. The two schemas have nothing to do
 * with each other beyond both being validated documents, and coupling them to
 * share ten lines would make an unrelated schema change ripple into this file
 * for no reason.
 */

import type { GameProgress, InProgress, LevelRecord, SaveDocument, Settings } from './save';
import { SAVE_SCHEMA_VERSION } from './save';

export interface Fault {
  path: string;
  problem: string;
}

export type Validated<T> = { ok: true; value: T } | { ok: false; faults: Fault[] };

export function describeFaults(faults: Fault[]): string {
  return faults
    .map((fault) => (fault.path ? `${fault.path}: ${fault.problem}` : fault.problem))
    .join('; ');
}

export type VersionVerdict = 'ok' | 'too-new' | 'too-old' | 'unreadable';

/**
 * `PROG-023` and `PROG-024`, the save-document analogue of `DATA-042`/`DATA-043`
 * in `level-guards.ts`. Checked before full validation for the same reason: a
 * newer-schema document is expected to fail shape validation (it may have grown
 * fields), and that failure must not be reported as corruption.
 */
export function checkSaveSchemaVersion(version: unknown): VersionVerdict {
  if (typeof version !== 'number' || !Number.isInteger(version)) return 'unreadable';
  if (version > SAVE_SCHEMA_VERSION) return 'too-new';
  if (version < SAVE_SCHEMA_VERSION) return 'too-old';
  return 'ok';
}

export type SaveMigration = (raw: Record<string, unknown>) => Record<string, unknown>;

/**
 * Keyed by the version a migration upgrades *from*. Empty today:
 * `SAVE_SCHEMA_VERSION` has had exactly one shape, so there is nothing yet to
 * migrate away from. Add an entry here, never edit an old one, when the shape
 * changes.
 */
export const SAVE_MIGRATIONS: Readonly<Record<number, SaveMigration>> = {};

/**
 * Applies registered migrations one version at a time until the document
 * reaches `SAVE_SCHEMA_VERSION`, or stops if a version in the chain has none
 * registered. The second case is a build that shipped a schema bump without a
 * migration for it -- a bug, not a state worth designing a message for -- so the
 * caller treats it the same as an unreadable document (`PROG-025`: retained, not
 * discarded, rather than half-applied).
 */
export function migrateSaveDocument(
  raw: Record<string, unknown>,
  fromVersion: number,
  migrations: Readonly<Record<number, SaveMigration>> = SAVE_MIGRATIONS,
): { ok: true; value: Record<string, unknown> } | { ok: false } {
  let doc = raw;
  let version = fromVersion;
  while (version < SAVE_SCHEMA_VERSION) {
    const migration = migrations[version];
    if (!migration) return { ok: false };
    doc = migration(doc);
    version += 1;
  }
  return { ok: true, value: doc };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function describe(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'an array';
  if (typeof value === 'string') return `the string ${JSON.stringify(value)}`;
  if (typeof value === 'number') return `the number ${String(value)}`;
  return `a value of type ${typeof value}`;
}

class Checker {
  readonly faults: Fault[] = [];

  add(path: string, problem: string): void {
    this.faults.push({ path, problem });
  }

  bool(value: unknown, path: string): boolean | null {
    if (typeof value !== 'boolean') {
      this.add(path, `expected a boolean, got ${describe(value)}`);
      return null;
    }
    return value;
  }

  text(value: unknown, path: string): string | null {
    if (typeof value !== 'string') {
      this.add(path, `expected a string, got ${describe(value)}`);
      return null;
    }
    return value;
  }

  nullableText(value: unknown, path: string): string | null | undefined {
    if (value === null) return null;
    return this.text(value, path);
  }

  int(value: unknown, path: string, min: number, max = Number.MAX_SAFE_INTEGER): number | null {
    if (typeof value !== 'number' || !Number.isInteger(value)) {
      this.add(path, `expected an integer, got ${describe(value)}`);
      return null;
    }
    if (value < min || value > max) {
      this.add(path, `expected an integer in ${String(min)}..${String(max)}, got ${String(value)}`);
      return null;
    }
    return value;
  }
}

function checkSettings(value: unknown, path: string, c: Checker): Settings | null {
  if (!isRecord(value)) {
    c.add(path, `expected an object, got ${describe(value)}`);
    return null;
  }
  const sound = c.bool(value.sound, `${path}.sound`);
  const music = c.bool(value.music, `${path}.music`);
  const reducedMotion = c.bool(value.reducedMotion, `${path}.reducedMotion`);
  if (sound === null || music === null || reducedMotion === null) return null;
  return { sound, music, reducedMotion };
}

function checkLevelRecord(value: unknown, path: string, c: Checker): LevelRecord | null {
  if (!isRecord(value)) {
    c.add(path, `expected an object, got ${describe(value)}`);
    return null;
  }
  const completed = c.bool(value.completed, `${path}.completed`);
  const stars = c.int(value.stars, `${path}.stars`, 0, 3);
  const bestScore = c.int(value.bestScore, `${path}.bestScore`, 0);
  const attempts = c.int(value.attempts, `${path}.attempts`, 0);
  const firstCompletedAt = c.nullableText(value.firstCompletedAt, `${path}.firstCompletedAt`);
  if (completed === null || stars === null || bestScore === null || attempts === null) return null;
  if (firstCompletedAt === undefined) return null;
  return { completed, stars, bestScore, attempts, firstCompletedAt };
}

function checkGameProgress(value: unknown, path: string, c: Checker): GameProgress | null {
  if (!isRecord(value)) {
    c.add(path, `expected an object, got ${describe(value)}`);
    return null;
  }
  const levelsValue = value.levels;
  if (!isRecord(levelsValue)) {
    c.add(`${path}.levels`, `expected an object, got ${describe(levelsValue)}`);
    return null;
  }
  const levels: Record<string, LevelRecord> = {};
  let levelsOk = true;
  for (const [levelId, recordValue] of Object.entries(levelsValue)) {
    const record = checkLevelRecord(recordValue, `${path}.levels.${levelId}`, c);
    if (record === null) {
      levelsOk = false;
      continue;
    }
    levels[levelId] = record;
  }
  const lastPlayedLevel = c.nullableText(value.lastPlayedLevel, `${path}.lastPlayedLevel`);
  if (!levelsOk || lastPlayedLevel === undefined) return null;
  return { levels, lastPlayedLevel };
}

function checkInProgress(value: unknown, path: string, c: Checker): InProgress | null | undefined {
  if (value === null) return null;
  if (!isRecord(value)) {
    c.add(path, `expected an object or null, got ${describe(value)}`);
    return undefined;
  }
  const gameId = c.text(value.gameId, `${path}.gameId`);
  const levelId = c.text(value.levelId, `${path}.levelId`);
  const savedAt = c.text(value.savedAt, `${path}.savedAt`);
  // `state` is opaque to the shell (save.ts) -- anything present is accepted.
  if (gameId === null || levelId === null || savedAt === null) return undefined;
  return { gameId, levelId, state: value.state, savedAt };
}

/**
 * `PROG-021`, `PROG-022`. The version check is the caller's, via
 * `checkSaveSchemaVersion` -- this only knows how to say a document is
 * malformed, not that it is from the future.
 */
export function validateSaveDocument(value: unknown): Validated<SaveDocument> {
  const c = new Checker();

  if (!isRecord(value)) {
    c.add('', `expected an object, got ${describe(value)}`);
    return { ok: false, faults: c.faults };
  }

  const schemaVersion = c.int(value.schemaVersion, 'schemaVersion', 1);
  const createdAt = c.text(value.createdAt, 'createdAt');
  const updatedAt = c.text(value.updatedAt, 'updatedAt');
  const settings = checkSettings(value.settings, 'settings', c);

  const gamesValue = value.games;
  const games: Record<string, GameProgress> = {};
  let gamesOk = true;
  if (!isRecord(gamesValue)) {
    c.add('games', `expected an object, got ${describe(gamesValue)}`);
    gamesOk = false;
  } else {
    for (const [gameId, progressValue] of Object.entries(gamesValue)) {
      const progress = checkGameProgress(progressValue, `games.${gameId}`, c);
      if (progress === null) {
        gamesOk = false;
        continue;
      }
      games[gameId] = progress;
    }
  }

  const inProgress = checkInProgress(value.inProgress, 'inProgress', c);

  if (
    schemaVersion === null ||
    createdAt === null ||
    updatedAt === null ||
    settings === null ||
    !gamesOk ||
    inProgress === undefined
  ) {
    return { ok: false, faults: c.faults };
  }

  return {
    ok: true,
    value: { schemaVersion, createdAt, updatedAt, settings, games, inProgress },
  };
}

/** `PROG-021`. `now` is injectable so a test can assert on the timestamp it produced. */
export function createDefaultSaveDocument(
  now: () => string = () => new Date().toISOString(),
): SaveDocument {
  const timestamp = now();
  return {
    schemaVersion: SAVE_SCHEMA_VERSION,
    createdAt: timestamp,
    updatedAt: timestamp,
    // No requirement fixes these; sound and music default on, reduced motion off,
    // because that is the more common preference to have to turn off rather than
    // on. The OS-level `prefers-reduced-motion` signal is `platform/motion.ts`'s
    // concern, applied on top of this once a settings screen exists to show it.
    settings: { sound: true, music: true, reducedMotion: false },
    games: {},
    inProgress: null,
  };
}
